import React from "react";

import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { Box } from "@mui/material";

import { BulletinMediaItem } from "@/components/alerts/add-v2/model/types";

import { AttachOption } from "./AttachMenu";
import { isViewable } from "./isViewable";
import { MediaRow } from "./MediaRow";

/**
 * The uploaded files, in `sortOrdinal` order — which the repository already applied, so this does
 * not re-sort. Two columns from sm up, one on a phone, as the design has it.
 *
 * Reordering is a card dropped on another card — the same drag that attaches a file to an entity,
 * just landing somewhere else. The **`DndContext` is the page's**, not this component's, because
 * that other landing place is in another section and dnd-kit only pairs a draggable with a droppable
 * inside one context. The order itself is a fractional `sort_ordinal` per file, PATCHed one file at
 * a time as the old flow's `SortableAttachments` does it.
 *
 * This was HTML5 drag-and-drop until 2026-08-21, which needed no context at all — and fired nothing
 * for touch or the keyboard, so a phone could not reorder and the handle was not tabbable. See
 * README decision 52 for what that cost and what it bought.
 */

interface MediaListProps {
  items: BulletinMediaItem[];
  /** Built once by the section: the same entity list for every row, checked per row. */
  entityOptions: (item: BulletinMediaItem) => AttachOption[];
  disabled?: boolean;
  onDelete: (item: BulletinMediaItem) => void;
  onToggleEntity: (item: BulletinMediaItem, entityId: string) => void;
  /** A thumbnail was clicked. Only ever called with a file the lightbox can show. */
  onExpand: (item: BulletinMediaItem) => void;
  /** Crop and rotate one image. */
  onEdit: (item: BulletinMediaItem) => void;
}

export const MediaList: React.FC<MediaListProps> = ({
  items,
  entityOptions,
  disabled,
  onDelete,
  onToggleEntity,
  onExpand,
  onEdit,
}) => (
  // A grid, so `rectSortingStrategy` rather than the vertical one the entity list uses. The context
  // itself is the page's, because the same drag can end on an entity card (decision 44).
  <SortableContext
    items={items.map((item) => item.id)}
    strategy={rectSortingStrategy}
  >
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
      }}
    >
      {items.map((item) => (
        <MediaRow
          key={item.id}
          item={item}
          entityOptions={entityOptions(item)}
          disabled={disabled}
          onDelete={() => onDelete(item)}
          onToggleEntity={(entityId) => onToggleEntity(item, entityId)}
          onExpand={isViewable(item) ? () => onExpand(item) : undefined}
          onEdit={() => onEdit(item)}
        />
      ))}
    </Box>
  </SortableContext>
);
